<script lang="ts" setup>
	import type { BatchUploadSteps } from '~/components/Uploads/batch.vue'
	import type { PaymentResponse } from '~/components/Gateway/channel-modal.vue'
	import { useResourcesApi } from '~/services/apis/resources'
	import { usePlansApi } from '~/services/apis/plan'

	const props = defineProps<{
		premium?: number
		uploadSteps: BatchUploadSteps
		previewImg: string
		fileName?: string
		amount: number
		categoryType?: string
		coverPeriod?: string | null
		promoCode?: string
	}>()
	const emits = defineEmits<{
		(event: 'fields', param: { [id: string]: unknown }): void
		(event: 'watchBreakdown', param: BatchPurchaseResponse | null): void
		(event: 'update:promoCode', param: string): void
		(event: 'submit', param: unknown): void
	}>()

	const { product_id } = useRoute().params
	const { readFile, downloadFileResource } = useFileExplorer()
	const { fetchStream, uploadProgress, error, pending } = useStreamReader()
	const { urlQuery, decryptString } = useUtils()
	const { CoverPeriods } = useProducts()
	const { getCoverPeriod, downloadBatchTemplate } = useResourcesApi()
	const { purchaseViaBatch } = usePlansApi()
	const { insuranceCategory } = useStore()

	const removalWarning = shallowRef(false)
	const failed = shallowRef(false)
	const downloadingTemplate = shallowRef(false)
	const paymentDialog = shallowRef(false)
	const submittingPurchase = shallowRef(false)
	const purchaseResult = ref<RecordSchema | null>(null)

	const formData = ref<RecordSchema>({
		file: null as null | File,
		product_id: product_id as string,
		cover_period: 'annual',
		payment_channel: '',
		transaction_id: '',
		sales_type: 'new purchase',
		promo_code: ''
	})
	const fieldIsEmpty = computed(() => {
		return !purchaseResult.value
	})
	const formUrl = computed(() => urlQuery.value?.form as string)
	const formUrlRecord = computed(() => {
		let formRecords: { [id: string]: string } = {}
		if (formUrl.value) {
			formRecords = decryptString(formUrl.value)
		}
		return formRecords
	})
	const campaignCode = computed(() => localStorage.getItem('campaign_code') || '')

	const setFormData = (template: RecordSchema) => {
		const formSetup = new FormData()
		for (const key in template) {
			formSetup.set(key, template[key])
		}
		return formSetup
	}

	const downLoadFile = async () => {
		try {
			downloadingTemplate.value = true
			const result = await downloadBatchTemplate(product_id as string)
			if (result?.size) {
				readFile(result, 'readAsDataURL', file => {
					const fileTarget = file?.target as FileReader
					if (fileTarget.result) {
						downloadFileResource(
							fileTarget.result as string,
							`${props.fileName || 'purchase-template'}.xlsx`
						)
					}
				})
			} else {
				useNotification().alert({
					status: 400,
					message: 'File Not Available For Download'
				})
			}
		} catch (e) {
			console.error(e)
		} finally {
			downloadingTemplate.value = false
		}
	}
	const computeFile = async () => {
		failed.value = false
		try {
			const template: RecordSchema = {
				product_id: product_id as string,
				cover_period: props.coverPeriod,
				file: formData.value.file as File,
				...formUrlRecord.value
			}
			const formSetup = setFormData(template)
			const result = await fetchStream<BatchPurchaseResponse>('/batch-purchase/compute-batch-purchase', {
				body: formSetup,
				methods: 'POST'
			})

			if (result?.data) {
				purchaseResult.value = result.data
				emits('watchBreakdown', result.data)
			}
			if (error.value) {
				failed.value = true
			}
		} catch (e) {
			console.error(e)
		}
	}
	const removeFile = (execute = false) => {
		removalWarning.value = false
		if (execute) {
			formData.value.file = null
			purchaseResult.value = null
			emits('watchBreakdown', null)
		} else {
			removalWarning.value = true
		}
	}
	const submitForm = async (param: PaymentResponse) => {
		try {
			submittingPurchase.value = true
			formData.value.transaction_id = param.transaction
			formData.value.payment_channel = param.channel
			const formSetup = setFormData({
				...formData.value,
				...formUrlRecord.value,
				promo_code: formData.value?.promo_code || campaignCode.value
			})
			const { data } = await purchaseViaBatch<{ id: string }>(formSetup)
			if (data.value?.data) {
				const response = data.value.data
				localStorage.removeItem('campaign_code')
				useNotification().alert({
					message: data.value.responseText,
					status: 200
				})
				navigateTo({
					query: {
						page: 'product-complete',
						active_plan_id: response.id
					}
				})
			}
		} catch (e) {
			console.error(e)
		} finally {
			submittingPurchase.value = false
		}
	}

	onMounted(() => {
		if (!CoverPeriods.length) {
			getCoverPeriod()
		}
	})
</script>

<template>
	<div class="w-full">
		<uploads-progress v-if="pending && !error" :file="formData.file" :progress="uploadProgress" />
		<div v-else-if="purchaseResult && !error" class="">
			<a-form
				:model="formData"
				:validate-messages="antValidateMessages"
				@finish="() => (paymentDialog = !!amount)"
			>
				<uploads-uploaded-card @remove="removeFile">
					<div class="flex w-full flex-col items-center justify-center">
						<img src="@/assets/images/png/excel-icon.png" :height="80" :width="80" alt="excel-icon" />
						<div class="mt-2 text-sm font-medium tracking-wide text-gray-500">
							{{ formData.file?.name || 'N/A' }}
						</div>
					</div>
				</uploads-uploaded-card>

				<template v-if="categoryType === 'auto'">
					<a-form-item class="label !my-5" required name="cover_period">
						<label for="label" title="Cover period">
							Cover period
							<span class="text-xs font-medium tracking-wide text-warning-500">
								(This is fixed for current purchase)
							</span>
						</label>

						<a-select value="annual" :options="CoverPeriods" disabled />
					</a-form-item>

					<a-form-item label="Category" class="!mb-4" name="vehicle_insurance_type" required>
						<a-select
							v-model:value="formData.vehicle_insurance_type as string"
							class="w-full"
							placeholder="Business Category Type"
							:options="insuranceCategory"
							@change="$emit('fields', { vehicle_insurance_type: formData.vehicle_insurance_type })"
						/>
					</a-form-item>
				</template>

				<div class="mt-5 border-t border-gray-200 pt-5">
					<a-form-item label="Do you have a promo code? (Optional)">
						<a-input
							v-model:value="formData.promo_code"
							class="custom-input w-full !bg-gray-50 !px-5"
							@change="() => $emit('update:promoCode', formData.promo_code)"
						/>
					</a-form-item>
				</div>

				<a-button
					html-type="submit"
					class="default-btn mt-10 w-full justify-center bg-primary-2"
					:disabled="fieldIsEmpty || !amount"
				>
					Continue
				</a-button>
			</a-form>
		</div>

		<template v-else>
			<uploads-batch
				v-model:file="formData.file"
				:preview-img-src="previewImg"
				:downloading="downloadingTemplate"
				:step-guide="uploadSteps"
				:upload-failed="error !== null"
				:re-upload-message="error || ''"
				@change="computeFile"
				@download="downLoadFile"
			/>
		</template>
		<loader-with-text :active="submittingPurchase" text="Submitting purchase" />
		<gateway-channel-modal
			v-model:show="paymentDialog"
			reason="new purchase"
			:withdraw-amount="amount"
			@payment-complete="submitForm"
		/>
		<dialogs-error
			v-model:show="removalWarning"
			type="warning"
			header="Delete File"
			wrapper-class="bg-gray-50 w-full md:py-8 py-3 md:px-5 rounded space-y-5"
		>
			<template #icon>
				<div class="light-shadow flex h-[80px] w-[80px] items-center justify-center rounded-full border">
					<lazy-tools-icon dir="fill" icon="warning" path-class="fill-warning-400" />
				</div>
			</template>

			<template #paragraph>
				<div class="">
					You are about to remove this file -
					<span class="font-medium text-primary-2"> {{ formData.file?.name }} </span>
					<br />
					You will have to re-upload this file again
				</div>
			</template>

			<template #button="{ close }">
				<div class="flex w-full items-center justify-between gap-x-8">
					<button
						type="button"
						class="default-btn flex-1 justify-center bg-gray-200 font-medium text-gray-950"
						@click="close"
					>
						Back
					</button>
					<button
						type="button"
						class="default-btn flex-1 justify-center bg-primary-2 font-medium tracking-wide"
						@click="removeFile(true)"
					>
						Delete file
					</button>
				</div>
			</template>
		</dialogs-error>
	</div>
</template>
